import { useMemo } from 'react';

import {
  Control,
  FieldValues,
  useController,
  useFormContext
} from 'react-hook-form';

import { Country } from '@common/types';
import { useProposalStore } from '@store/proposal';
import { PhoneNumberSelectGroup, PhoneNumberSelectGroupProps } from '@ui';
import { COUNTRY_INDIA } from '@utils/constants';

export interface PhoneNumberFieldWithContextProps
  extends Omit<
    PhoneNumberSelectGroupProps,
    'value' | 'countryValue' | 'onChange' | 'onCountryChange' | 'countries' | 'name'
  > {
  mobileFieldName: string;
  countryFieldName: string;
  countries?: Country[];
  control?: Control<FieldValues>;
}

/**
 * PhoneNumberFieldWithContext - Form-integrated phone number field with country selection
 *
 * This component integrates PhoneNumberSelectGroup with React Hook Form and accesses
 * the proposal store for country data. It uses dual field storage pattern: separate
 * fields for phone number and country object.
 *
 * Key features:
 * - Automatic country list from proposal store (can be overridden via props)
 * - Dynamic maxLength calculation based on selected country
 * - Auto-validation when country changes
 * - Stores full country object (solves USA/Canada +1 ambiguity)
 * - Defaults to India when no country is set
 *
 * @example
 * <PhoneNumberFieldWithContext
 *   mobileFieldName="basic.mobile"
 *   countryFieldName="basic.country"
 *   label="Mobile Number"
 *   placeholder="Enter mobile number"
 * />
 *
 * Form fields:
 * {
 *   basic: {
 *     mobile: "9840471601",
 *     country: { name: "India", calling_code: "91", code: "IN" }
 *   }
 * }
 */
export const PhoneNumberFieldWithContext = (
  props: PhoneNumberFieldWithContextProps
) => {
  const {
    mobileFieldName,
    countryFieldName,
    countries: overrideCountries,
    control: controlProp,
    ...uiProps
  } = props;
  let formContextControl: Control<FieldValues> | undefined;
  try {
    const methods = useFormContext();
    formContextControl = methods?.control as Control<FieldValues> | undefined;
  } catch {
    formContextControl = undefined;
  }

  const control = controlProp ?? formContextControl;

  // Register both fields with React Hook Form
  const { field: phoneField, fieldState: phoneState } = useController({
    name: mobileFieldName,
    control
  });

  const { field: countryField, fieldState: countryState } = useController({
    name: countryFieldName,
    control,
    defaultValue: COUNTRY_INDIA
  });

  // Get countries from store
  const proposalCountries = useProposalStore((state) => state.countryCallingCodes);
  const countries = overrideCountries?.length
    ? overrideCountries
    : proposalCountries;

  const resolvedCountries = useMemo(() => {
    if (countries && countries.length) return countries;
    return [COUNTRY_INDIA];
  }, [countries]);

  const maxLength = useMemo(() => {
    if (!countryField.value?.calling_code) return 14;
    const callingCode = countryField.value.calling_code;
    return callingCode === '91' ? 10 : 14 - callingCode.length;
  }, [countryField.value?.calling_code]);

  const handleCountryChange = (country: Country) => {
    countryField.onChange(country);
    if (phoneState.isDirty || phoneState.isTouched || phoneState.invalid) {
      setTimeout(() => {
        phoneField.onBlur();
      }, 0);
    }
  };

  return (
    <PhoneNumberSelectGroup
      name={mobileFieldName}
      value={phoneField.value || ''}
      countryValue={countryField.value}
      onChange={phoneField.onChange}
      onCountryChange={handleCountryChange}
      onBlur={phoneField.onBlur}
      error={phoneState.error?.message || countryState.error?.message}
      countries={resolvedCountries}
      maxLength={maxLength}
      {...uiProps}
    />
  );
};
